import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { useLocation } from 'react-router-dom';
import { MobileHeader } from '../components/mobile';
import BuildingBanner from '../components/BuildingBanner';
import { Play, X, Video, Mic, MessageSquare, Users, Check, AlertCircle, Building2, Plus, Camera } from 'lucide-react';
import AttendanceSheet from '../components/AttendanceSheet';
import DelegationForm from '../components/DelegationForm';
import GuidedTour, { TOUR_CONFIGS } from '../components/GuidedTour';
import { SuccessNotification, SUCCESS_CONFIGS } from '../components/SuccessNotification';
import { Daily360MeetingRoom, DualCamera360MeetingRoom } from '../components/video';
import {
  AGList,
  AGInfoTab,
  AGDocumentsTab,
  AGResolutionsTab,
  AGCorrespondanceVoteTab,
  AGMeetingRoom,
  AGReportTab,
  NewAGModal,
  NewResolutionModal,
  VoteParCorrespondance,
  VPCGestionnairePanel,
  AGCloseButton
} from '../components/assemblees';
import PVSignatures from '../components/assemblees/PVSignatures';
import '../components/assemblees/PVSignatures.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Assemblees = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const location = useLocation();
  const [assemblees, setAssemblees] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedAG, setSelectedAG] = useState(null);
  const [activeTab, setActiveTab] = useState('informations');
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showNewResolutionModal, setShowNewResolutionModal] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    building_id: '',
    titre: '',
    description: '',
    date: '',
    heure: '19:00',
    lieu: '',
    complement_lieu: ''
  });

  // Participation state
  const [hasConfirmedParticipation, setHasConfirmedParticipation] = useState(false);
  const [participationMode, setParticipationMode] = useState(null);
  const [isVideoConnected, setIsVideoConnected] = useState(false);
  const [dailyToken, setDailyToken] = useState(null);
  const [agClosedForceDisconnect, setAgClosedForceDisconnect] = useState(false);

  const [resolutionForm, setResolutionForm] = useState({
    titre: '',
    description: '',
    clef_repartition: 'Clef 1',
    type_majorite: 'Simple - Art. 24',
    resultat: 'En attente'
  });

  // Delegation modal state
  const [showDelegationModal, setShowDelegationModal] = useState(false);

  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successConfig, setSuccessConfig] = useState(null);
  const [createdAGId, setCreatedAGId] = useState(null);

  // Fetch Daily.co meeting token with user name
  const fetchDailyToken = useCallback(async (roomUrl, agId, forceReconnect = false) => {
    if (!roomUrl || !token) return;
    try {
      // If force reconnect, first disconnect previous session
      if (forceReconnect) {
        try {
          await axios.post(`${API}/daily/force-disconnect`, 
            { assemblee_id: agId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) {
          console.log('Force disconnect failed:', e);
        }
      }
      
      // Extract room name from URL
      const roomName = roomUrl.split('/').pop();
      const response = await axios.post(`${API}/daily/meeting-token`, 
        { room_name: roomName, assemblee_id: agId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.token) {
        setDailyToken(response.data.token);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        // User already connected - offer to force reconnect
        const shouldReconnect = window.confirm(
          'Vous êtes déjà connecté à cette réunion depuis un autre appareil ou onglet.\n\n' +
          'Voulez-vous vous déconnecter de l\'autre session et vous reconnecter ici ?'
        );
        if (shouldReconnect) {
          // Retry with force reconnect
          await fetchDailyToken(roomUrl, agId, true);
        }
      } else if (err.response?.status === 403) {
        // AG is closed
        console.log('AG is closed, cannot connect');
      } else {
        console.log('Could not get meeting token, using fallback');
      }
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reset selectedAG when navigating to /assemblees via menu
  useEffect(() => {
    // If the URL is exactly /assemblees (no params), reset to list view
    // Using location.key ensures this triggers even when clicking the same menu item
    if (location.pathname === '/assemblees' && !location.search) {
      setSelectedAG(null);
      setActiveTab('informations');
    }
  }, [location.pathname, location.search, location.key]);

  // Fetch token when AG with room_url is selected
  useEffect(() => {
    if (selectedAG?.room_url && selectedAG?.id) {
      fetchDailyToken(selectedAG.room_url, selectedAG.id);
    }
  }, [selectedAG?.room_url, selectedAG?.id, fetchDailyToken]);

  // Cleanup: mark user as disconnected when leaving page or changing AG
  useEffect(() => {
    let currentAgId = selectedAG?.id;
    
    const markDisconnect = async (agId) => {
      if (!agId || !token) return;
      try {
        await axios.post(`${API}/daily/leave-meeting`, 
          { assemblee_id: agId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.log('Error marking disconnect:', err);
      }
    };

    const handleBeforeUnload = () => {
      if (currentAgId && token) {
        // Synchronous XHR for beforeunload (sendBeacon doesn't support headers)
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/daily/leave-meeting`, false); // synchronous
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(JSON.stringify({ assemblee_id: currentAgId }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Call disconnect when component unmounts or AG changes
      markDisconnect(currentAgId);
    };
  }, [selectedAG?.id, token]);

  // CRITICAL: Poll for AG closure and force disconnect
  useEffect(() => {
    if (!selectedAG?.id || !token) return;
    if (selectedAG.status === 'Clôturée') return; // Already closed
    
    const checkClosure = async () => {
      try {
        const response = await axios.get(`${API}/assemblees/${selectedAG.id}/connection-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.is_closed || response.data.must_disconnect) {
          console.log('AG CLOSED - Force disconnect triggered');
          setAgClosedForceDisconnect(true);
          setDailyToken(null); // Clear token to prevent reconnection
          
          // Refresh AG data to update UI
          fetchData();
        }
      } catch (err) {
        if (err.response?.data?.detail?.includes('AG_CLOSED')) {
          setAgClosedForceDisconnect(true);
          setDailyToken(null);
        }
      }
    };
    
    // Poll every 3 seconds
    const closureInterval = setInterval(checkClosure, 3000);
    
    return () => clearInterval(closureInterval);
  }, [selectedAG?.id, selectedAG?.status, token]);

  // Check participation when AG is selected
  const checkParticipation = useCallback(async (agId) => {
    if (!agId || !token || user?.role !== 'coproprietaire') return;
    try {
      const response = await axios.get(`${API}/assemblees/${agId}/my-participation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.confirmed) {
        setHasConfirmedParticipation(true);
        setParticipationMode(response.data.mode);
      } else {
        setHasConfirmedParticipation(false);
        setParticipationMode(null);
      }
    } catch (err) {
      setHasConfirmedParticipation(false);
      setParticipationMode(null);
    }
  }, [token, user?.role]);

  // Check if we can still modify participation (> 24h before AG)
  const canModifyParticipation = useCallback(() => {
    if (!selectedAG?.date_heure) return false;
    const agDate = new Date(selectedAG.date_heure);
    const now = new Date();
    const hoursUntilAG = (agDate - now) / (1000 * 60 * 60);
    return hoursUntilAG > 24;
  }, [selectedAG?.date_heure]);

  // Get time remaining until modification deadline
  const getModificationDeadline = useCallback(() => {
    if (!selectedAG?.date_heure) return null;
    const agDate = new Date(selectedAG.date_heure);
    const deadline = new Date(agDate.getTime() - 24 * 60 * 60 * 1000); // 24h before
    return deadline;
  }, [selectedAG?.date_heure]);

  // State for editing mode
  const [isEditingParticipation, setIsEditingParticipation] = useState(false);

  // Confirm participation
  const confirmParticipation = async (mode) => {
    if (!selectedAG?.id) return;
    
    // If delegation mode, open the delegation modal instead of confirming immediately
    if (mode === 'delegation') {
      setShowDelegationModal(true);
      return;
    }
    
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/participate`, {
        mode: mode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasConfirmedParticipation(true);
      setParticipationMode(mode);
      setIsEditingParticipation(false); // Close edit mode after saving
    } catch (err) {
      console.error('Error confirming participation:', err);
      alert('Erreur lors de la confirmation de participation');
    }
  };

  // Handle delegation success
  const handleDelegationSuccess = async () => {
    // Confirm participation with delegation mode after successful delegation
    try {
      await axios.post(`${API}/assemblees/${selectedAG.id}/participate`, {
        mode: 'delegation'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasConfirmedParticipation(true);
      setParticipationMode('delegation');
    } catch (err) {
      console.error('Error confirming delegation participation:', err);
    }
  };

  const fetchData = async () => {
    try {
      const [assembleesRes, buildingsRes] = await Promise.all([
        axios.get(`${API}/assemblees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/immeubles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAssemblees(assembleesRes.data);
      setBuildings(buildingsRes.data);
      
      // Check if we need to auto-select an AG from navigation state
      if (location.state?.selectedAG) {
        const agId = location.state.selectedAG;
        // Find the AG in the list and fetch its details
        const ag = assembleesRes.data.find(a => a.id === agId);
        if (ag) {
          fetchAGDetail(agId);
        }
        // Clear the location state to avoid re-selecting on subsequent renders
        window.history.replaceState({}, document.title);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAGDetail = async (agId) => {
    try {
      const response = await axios.get(`${API}/assemblees/${agId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedAG(response.data);
      // Also check participation status
      await checkParticipation(agId);
    } catch (error) {
      console.error('Error fetching AG detail:', error);
    }
  };

  const handleCreateAG = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/assemblees`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowNewModal(false);
      setFormData({
        building_id: '',
        titre: '',
        description: '',
        date: '',
        heure: '19:00',
        lieu: '',
        complement_lieu: ''
      });
      fetchData();
      
      // Show success notification
      const newAgId = response.data.id;
      setCreatedAGId(newAgId);
      setSuccessConfig({
        ...SUCCESS_CONFIGS.AG_CREATED,
        nextStep: {
          ...SUCCESS_CONFIGS.AG_CREATED.nextStep,
          description: 'Ajoutez les résolutions à voter lors de cette assemblée.',
          buttonText: 'Voir l\'assemblée',
          path: null // We'll handle navigation manually
        }
      });
      setShowSuccessNotification(true);
    } catch (error) {
      console.error('Error creating AG:', error);
    }
  };

  // Handle success notification next step
  const handleSuccessNextStep = async () => {
    if (createdAGId) {
      await fetchAGDetail(createdAGId);
      setCreatedAGId(null);
    }
    setShowSuccessNotification(false);
  };

  const handleUpdateAG = async () => {
    if (!selectedAG) return;

    try {
      await axios.put(
        `${API}/assemblees/${selectedAG.id}`,
        {
          titre: selectedAG.titre,
          description: selectedAG.description,
          date: selectedAG.date,
          heure: selectedAG.heure,
          lieu: selectedAG.lieu,
          complement_lieu: selectedAG.complement_lieu
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Assemblée mise à jour avec succès');
    } catch (error) {
      console.error('Error updating AG:', error);
    }
  };

  const handleDeleteAG = async (agId) => {
    try {
      await axios.delete(`${API}/assemblees/${agId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh the list
      fetchData();
      // If the deleted AG was selected, clear selection
      if (selectedAG?.id === agId) {
        setSelectedAG(null);
      }
    } catch (error) {
      console.error('Error deleting AG:', error);
      throw error; // Re-throw to let the component handle the error
    }
  };

  const handleCreateResolution = async (e) => {
    e.preventDefault();
    if (!selectedAG) return;

    try {
      await axios.post(
        `${API}/assemblees/${selectedAG.id}/resolutions`,
        {
          ...resolutionForm,
          numero: (selectedAG.resolutions?.length || 0) + 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowNewResolutionModal(false);
      setResolutionForm({
        titre: '',
        description: '',
        clef_repartition: 'Clef 1',
        type_majorite: 'Simple - Art. 24',
        resultat: 'En attente'
      });
      fetchAGDetail(selectedAG.id);
    } catch (error) {
      console.error('Error creating resolution:', error);
    }
  };

  const handleStartAG = async (mode = 'standard', thetaStream = null) => {
    try {
      // First, create the Daily.co room for video conferencing
      const roomSuffix = mode === '360' ? '-360' : '';
      const roomName = `syndicappli-ag-${selectedAG.id}${roomSuffix}`.replace(/_/g, '-').toLowerCase().substring(0, 40);
      
      const roomResponse = await axios.post(
        `${API}/daily/rooms`,
        { 
          room_name: roomName, 
          assemblee_id: selectedAG.id,
          room_type: mode // 'standard' or '360'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const roomUrl = roomResponse.data.room_url;
      
      // Update AG status and store the room URL with room type
      await axios.put(
        `${API}/assemblees/${selectedAG.id}`,
        { 
          status: 'Ouverte',
          room_url: roomUrl,
          room_type: mode  // Store room type for participants to know
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchAGDetail(selectedAG.id);
    } catch (error) {
      console.error('Error starting AG:', error);
      alert('Erreur lors du démarrage de l\'AG. Veuillez réessayer.');
    }
  };

  const handleSelectAG = (ag) => {
    setSelectedAG(ag);
    fetchAGDetail(ag.id);
  };

  const tabs = [
    { id: 'informations', label: 'Informations' },
    { id: 'documents', label: 'Documents' },
    { id: 'resolutions', label: 'Résolutions' },
    { id: 'feuille', label: 'Feuille de présence' },
    { id: 'correspondance', label: 'Vote par correspondance' },
    { id: 'salle', label: 'Salle de réunion' },
    { id: 'compte-rendu', label: 'Compte-rendu' }
  ];

  if (loading) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title="Assemblées Générales"
            onMenuClick={onMenuClick}
          />
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
        </div>
      </>
    );
  }

  // Show list view if no AG selected
  if (!selectedAG) {
    return (
      <>
        {isMobile && (
          <MobileHeader 
            title="Assemblées Générales"
            onMenuClick={onMenuClick}
            showSearch={true}
          />
        )}
        <div className={isMobile ? 'p-4' : ''}>
          {/* Guided Tour for Assemblées */}
          {!isMobile && <GuidedTour {...TOUR_CONFIGS.assemblees} />}
          
          {/* Building Banner for Copropriétaires */}
          {user?.role === 'coproprietaire' && (
            <BuildingBanner token={token} user={user} compact={true} />
          )}
          <div data-tour="ag-list">
            <AGList
              assemblees={assemblees}
              userRole={user?.role}
              buildings={buildings}
              selectedBuilding={selectedBuilding}
              onBuildingChange={setSelectedBuilding}
              onSelectAG={handleSelectAG}
              onNewAG={() => setShowNewModal(true)}
              onDeleteAG={handleDeleteAG}
              isMobile={isMobile}
            />
          </div>
        </div>
        
        {showNewModal && (
          <NewAGModal
            formData={formData}
            setFormData={setFormData}
            buildings={buildings}
            onSubmit={handleCreateAG}
            onClose={() => setShowNewModal(false)}
          />
        )}

        {/* Success Notification for AG Creation (list view) */}
        {successConfig && (
          <SuccessNotification
            isOpen={showSuccessNotification}
            onClose={() => {
              setShowSuccessNotification(false);
              setSuccessConfig(null);
            }}
            title={successConfig.title}
            message={successConfig.message}
            nextStep={successConfig.nextStep ? {
              ...successConfig.nextStep,
              buttonText: successConfig.nextStep.buttonText,
              description: successConfig.nextStep.description,
              path: null // We handle navigation manually
            } : null}
            notificationType="AG_CREATED"
            autoCloseDelay={10000}
          />
        )}
      </>
    );
  }

  // Show detail view
  return (
    <>
      {isMobile && (
        <MobileHeader 
          title={selectedAG?.titre || 'Assemblée'}
          onMenuClick={onMenuClick}
          showBackButton={true}
          rightAction={
            <button
              onClick={() => setSelectedAG(null)}
              className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          }
        />
      )}
    <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`} data-testid="ag-detail">
      {/* Back button - Hidden on mobile (using header back) */}
      {!isMobile && (
        <div className="mb-4">
          <button
            onClick={() => setSelectedAG(null)}
            className="text-[#00D3A8] hover:text-[#00C7A1] flex items-center space-x-1"
            data-testid="back-to-list-btn"
          >
            <span>←</span>
            <span>Retour à la liste des AG</span>
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">{selectedAG.titre}</h2>
            <div className="flex items-center space-x-3">
              {user?.role === 'gestionnaire' && selectedAG.status === 'Publiée' && (
                <button 
                  onClick={handleStartAG}
                  className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
                  data-testid="start-ag-header-btn"
                >
                  <Play size={20} />
                  <span>Démarrer</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Participation Banner for Copropriétaires - Shows for Publiée or Ouverte status */}
        {user?.role === 'coproprietaire' && (selectedAG.status === 'Publiée' || selectedAG.status === 'Ouverte' || selectedAG.status === 'En cours') && (
          <div className="p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-[#00D3A8]/5 to-transparent">
            {(!hasConfirmedParticipation || isEditingParticipation) ? (
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {isEditingParticipation ? 'Modifier votre mode de participation' : 'Confirmez votre participation'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {isEditingParticipation 
                        ? 'Choisissez votre nouveau mode de participation :'
                        : 'Pour être inscrit sur la feuille de présence et pouvoir voter, choisissez votre mode de participation :'
                      }
                    </p>
                    {canModifyParticipation() && (
                      <p className="text-xs text-amber-600 mt-1">
                        {"⏰ Vous pouvez modifier jusqu'au "}{getModificationDeadline()?.toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                        })}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <button
                    onClick={() => confirmParticipation('video')}
                    className={`p-3 border-2 rounded-xl transition-all text-center ${
                      participationMode === 'video' 
                        ? 'border-[#00D3A8] bg-[#00D3A8] text-white' 
                        : 'border-[#00D3A8] bg-[#00D3A8]/5 hover:bg-[#00D3A8]/10'
                    }`}
                    data-testid="participate-video-btn"
                  >
                    <Video className={`mx-auto mb-1 ${participationMode === 'video' ? 'text-white' : 'text-[#00D3A8]'}`} size={24} />
                    <span className={`font-medium text-sm block ${participationMode === 'video' ? 'text-white' : 'text-gray-800'}`}>Vidéo</span>
                  </button>
                  
                  <button
                    onClick={() => confirmParticipation('audio')}
                    className={`p-3 border-2 rounded-xl transition-all text-center ${
                      participationMode === 'audio' 
                        ? 'border-blue-500 bg-blue-500 text-white' 
                        : 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                    }`}
                    data-testid="participate-audio-btn"
                  >
                    <Mic className={`mx-auto mb-1 ${participationMode === 'audio' ? 'text-white' : 'text-blue-500'}`} size={24} />
                    <span className={`font-medium text-sm block ${participationMode === 'audio' ? 'text-white' : 'text-gray-800'}`}>Audio</span>
                  </button>
                  
                  <button
                    onClick={() => confirmParticipation('presentiel')}
                    className={`p-3 border-2 rounded-xl transition-all text-center ${
                      participationMode === 'presentiel' 
                        ? 'border-orange-500 bg-orange-500 text-white' 
                        : 'border-orange-500 bg-orange-50 hover:bg-orange-100'
                    }`}
                    data-testid="participate-presentiel-btn"
                  >
                    <Building2 className={`mx-auto mb-1 ${participationMode === 'presentiel' ? 'text-white' : 'text-orange-500'}`} size={24} />
                    <span className={`font-medium text-sm block ${participationMode === 'presentiel' ? 'text-white' : 'text-gray-800'}`}>Présentiel</span>
                  </button>
                  
                  <button
                    onClick={() => confirmParticipation('correspondance')}
                    className={`p-3 border-2 rounded-xl transition-all text-center ${
                      participationMode === 'correspondance' 
                        ? 'border-amber-500 bg-amber-500 text-white' 
                        : 'border-amber-500 bg-amber-50 hover:bg-amber-100'
                    }`}
                    data-testid="participate-correspondance-btn"
                  >
                    <MessageSquare className={`mx-auto mb-1 ${participationMode === 'correspondance' ? 'text-white' : 'text-amber-500'}`} size={24} />
                    <span className={`font-medium text-sm block ${participationMode === 'correspondance' ? 'text-white' : 'text-gray-800'}`}>Correspondance</span>
                  </button>
                  
                  <button
                    onClick={() => confirmParticipation('delegation')}
                    className={`p-3 border-2 rounded-xl transition-all text-center ${
                      participationMode === 'delegation' 
                        ? 'border-purple-500 bg-purple-500 text-white' 
                        : 'border-purple-500 bg-purple-50 hover:bg-purple-100'
                    }`}
                    data-testid="participate-delegation-btn"
                  >
                    <Users className={`mx-auto mb-1 ${participationMode === 'delegation' ? 'text-white' : 'text-purple-500'}`} size={24} />
                    <span className={`font-medium text-sm block ${participationMode === 'delegation' ? 'text-white' : 'text-gray-800'}`}>Délégation</span>
                  </button>
                </div>
                
                {isEditingParticipation && (
                  <button
                    onClick={() => setIsEditingParticipation(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Annuler
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="text-green-600" size={20} />
                  </div>
                  <div>
                    <span className="font-semibold text-green-800">Participation confirmée</span>
                    <span className="text-green-700 text-sm block">
                      Mode : {participationMode === 'video' ? 'Vidéoconférence' : 
                              participationMode === 'audio' ? 'Audioconférence' :
                              participationMode === 'presentiel' ? 'Présentiel (en salle)' :
                              participationMode === 'correspondance' ? 'Vote par correspondance' : 'Délégation de pouvoir'}
                    </span>
                  </div>
                </div>
                
                {/* Bouton Modifier - visible si > 24h avant l'AG */}
                {canModifyParticipation() ? (
                  <button
                    onClick={() => setIsEditingParticipation(true)}
                    className="px-4 py-2 text-sm font-medium text-[#00D3A8] border border-[#00D3A8] rounded-lg hover:bg-[#00D3A8]/10 transition-colors"
                    data-testid="modify-participation-btn"
                  >
                    Modifier
                  </button>
                ) : (
                  <span className="text-xs text-gray-500 italic">
                    {"Modification impossible (- de 24h avant l'AG)"}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tabs - Style unifié avec coins arrondis en haut */}
        <div className="border-b border-gray-200 px-4 md:px-6">
          <div className="flex flex-wrap gap-1 items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium transition-all rounded-t-lg ${
                    activeTab === tab.id
                      ? 'bg-[#00D3A8] text-white'
                      : 'text-gray-600 hover:bg-gray-100 bg-gray-50'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-3 py-2">
              {/* Add Resolution Button - Only for gestionnaire when on resolutions tab and AG is open */}
              {user?.role === 'gestionnaire' && activeTab === 'resolutions' && (selectedAG?.status === 'En cours' || selectedAG?.status === 'Ouverte' || selectedAG?.status === 'Publiée') && (
                <button
                  onClick={() => setShowNewResolutionModal(true)}
                  className="flex items-center space-x-2 bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-lg transition-colors text-sm font-medium"
                  data-testid="add-resolution-header-btn"
                >
                  <Plus size={18} />
                  <span>Ajouter résolution</span>
                </button>
              )}
              
              {/* Close AG Button - Only for gestionnaire when AG is open */}
              {user?.role === 'gestionnaire' && (selectedAG?.status === 'En cours' || selectedAG?.status === 'Ouverte') && (
                <AGCloseButton
                  selectedAG={selectedAG}
                  token={token}
                  bureau={selectedAG?.bureau}
                  onAGClosed={(data) => {
                    // Refresh AG data after closure
                    fetchData();
                    if (selectedAG) {
                      setSelectedAG({ ...selectedAG, status: 'Clôturée' });
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* AG Closed Force Disconnect Modal */}
        {agClosedForceDisconnect && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="text-red-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Assemblée Générale Clôturée</h3>
              <p className="text-gray-600 mb-4">
                L'Assemblée Générale a été clôturée par le gestionnaire. 
                Votre connexion a été terminée.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Il n'est plus possible de se reconnecter à cette AG.
                Le procès-verbal sera disponible prochainement.
              </p>
              <button
                onClick={() => {
                  setAgClosedForceDisconnect(false);
                  setSelectedAG(null);
                  fetchData();
                }}
                className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-3 px-6 rounded-lg transition-colors"
              >
                Retour à la liste des AG
              </button>
            </div>
          </div>
        )}

        {/* Persistent Video Conference - Always visible when AG is open */}
        {selectedAG?.status === 'Ouverte' && selectedAG.room_url && !agClosedForceDisconnect && (
          (user?.role === 'gestionnaire' || 
           (hasConfirmedParticipation && (participationMode === 'video' || participationMode === 'audio'))) && (
          <div className="border-b border-gray-200 bg-gray-900">
            <div className="relative" style={{ height: activeTab === 'salle' ? '350px' : '180px' }}>
              {/* 360° mode badge */}
              {selectedAG.room_type === '360' && (
                <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
                  <span className="px-3 py-1 bg-[#00D3A8] text-white text-sm font-medium rounded-full flex items-center gap-1">
                    <Camera size={14} />
                    360° THETA
                  </span>
                  {user?.role !== 'gestionnaire' && (
                    <span className="text-xs text-white/80 bg-black/40 px-2 py-1 rounded">
                      Glissez pour naviguer
                    </span>
                  )}
                </div>
              )}
              
              {/* Use DualCamera360MeetingRoom for 360° rooms (webcam + THETA), iframe for standard */}
              {selectedAG.room_type === '360' && dailyToken ? (
                <DualCamera360MeetingRoom
                  roomUrl={selectedAG.room_url}
                  token={dailyToken}
                  userName={`${user?.prenom || ''} ${user?.nom || ''}`.trim()}
                  isHost={user?.role === 'gestionnaire'}
                  thetaStream={selectedAG.thetaStream || null}
                  onLeave={() => console.log('Left meeting')}
                  onError={(err) => console.error('Meeting error:', err)}
                />
              ) : selectedAG.room_type === '360' && !dailyToken ? (
                <div className="flex items-center justify-center h-full bg-gray-900">
                  <div className="text-center text-white">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00D3A8] mx-auto mb-4"></div>
                    <p className="text-sm">Préparation de la vue 360°...</p>
                  </div>
                </div>
              ) : (
                <iframe
                  src={dailyToken 
                    ? `${selectedAG.room_url}?t=${dailyToken}&showLeaveButton=true&showFullscreenButton=true&activeSpeakerMode=true&showParticipantsBar=true&layout=browser&startKnocking=false` 
                    : `${selectedAG.room_url}?name=${encodeURIComponent(`${user?.prenom || ''} ${user?.nom || ''}`.trim())}&showLeaveButton=true&showFullscreenButton=true&activeSpeakerMode=true&showParticipantsBar=true&layout=browser`
                  }
                  allow="camera; microphone; fullscreen; display-capture"
                  className="w-full h-full"
                  style={{ border: 'none' }}
                  data-testid="daily-meeting-iframe-persistent"
                />
              )}
              
              {/* Minimize/Expand button */}
              {activeTab !== 'salle' && (
                <button
                  onClick={() => setActiveTab('salle')}
                  className="absolute bottom-2 right-2 bg-[#00D3A8] text-white px-3 py-1 rounded-lg text-sm hover:bg-[#00C7A1] flex items-center space-x-1"
                >
                  <span>Agrandir</span>
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Tab Content */}
        <div className="p-4 md:p-6">
          {activeTab === 'informations' && (
            <AGInfoTab
              selectedAG={selectedAG}
              setSelectedAG={setSelectedAG}
              userRole={user?.role}
              onUpdate={handleUpdateAG}
              token={token}
            />
          )}

          {activeTab === 'documents' && (
            <AGDocumentsTab
              assemblee={selectedAG}
              userRole={user?.role}
              token={token}
              onDocumentsChange={() => fetchAGDetail(selectedAG.id)}
            />
          )}

          {activeTab === 'resolutions' && (
            <AGResolutionsTab
              selectedAG={selectedAG}
              userRole={user?.role}
              token={token}
              onNewResolution={() => setShowNewResolutionModal(true)}
              onVoteComplete={() => fetchAGDetail(selectedAG.id)}
              hasConfirmedParticipation={hasConfirmedParticipation}
              participationMode={participationMode}
              onResolutionsImported={() => fetchAGDetail(selectedAG.id)}
            />
          )}

          {activeTab === 'feuille' && (
            <AttendanceSheet
              assembleeId={selectedAG.id}
              token={token}
              userRole={user?.role}
              agInfo={selectedAG}
              currentUserId={user?.id}
            />
          )}

          {activeTab === 'correspondance' && (
            <>
              {/* VPC pour coproprietaires */}
              {user?.role === 'coproprietaire' && (
                <VoteParCorrespondance
                  assemblee={selectedAG}
                  user={user}
                  token={token}
                  onVoteSubmitted={() => fetchData()}
                />
              )}
              
              {/* VPC gestion pour gestionnaires */}
              {user?.role === 'gestionnaire' && (
                <VPCGestionnairePanel
                  assemblee={selectedAG}
                  token={token}
                  onUpdate={() => fetchData()}
                />
              )}
              
              {/* Message pour autres rôles */}
              {user?.role !== 'coproprietaire' && user?.role !== 'gestionnaire' && (
                <div className="bg-white rounded-xl p-8 text-center">
                  <Users className="mx-auto text-gray-400 mb-4" size={48} />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Réservé aux coproprietaires</h3>
                  <p className="text-gray-500">
                    Cette section permet aux coproprietaires de voter par correspondance avant l'AG.
                  </p>
                </div>
              )}
            </>
          )}

          {activeTab === 'salle' && (
            <AGMeetingRoom
              selectedAG={selectedAG}
              user={user}
              token={token}
              onStartAG={handleStartAG}
              hasConfirmedParticipation={hasConfirmedParticipation}
              participationMode={participationMode}
              onParticipationConfirmed={(mode) => {
                setHasConfirmedParticipation(true);
                setParticipationMode(mode);
              }}
              isVideoPersistent={
                selectedAG?.status === 'Ouverte' && 
                hasConfirmedParticipation && 
                (participationMode === 'video' || participationMode === 'audio')
              }
              onUpdateAG={(updatedAG) => {
                setSelectedAG(updatedAG);
              }}
              onRefreshAG={() => fetchAGDetail(selectedAG.id)}
            />
          )}

          {activeTab === 'compte-rendu' && (
            <AGReportTab 
              selectedAG={selectedAG}
              token={token}
              userRole={user?.role} 
            />
          )}

          {/* PV Signatures - Show for closed AGs */}
          {(selectedAG?.status === 'Passée' || selectedAG?.status === 'Clôturée') && 
           user?.role === 'gestionnaire' && (
            <PVSignatures
              assemblee={selectedAG}
              token={token}
              userRole={user?.role}
              userId={user?.id}
              onSignComplete={(data) => {
                console.log('Signature completed:', data);
                // Refresh AG data
                fetchAGDetail(selectedAG.id);
              }}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showNewResolutionModal && (
        <NewResolutionModal
          formData={resolutionForm}
          setFormData={setResolutionForm}
          onSubmit={handleCreateResolution}
          onClose={() => setShowNewResolutionModal(false)}
        />
      )}

      {/* Delegation Modal */}
      {showDelegationModal && (
        <DelegationForm
          assembleeId={selectedAG?.id}
          token={token}
          user={user}
          onClose={() => setShowDelegationModal(false)}
          onSuccess={handleDelegationSuccess}
        />
      )}

      {/* Success Notification */}
      {successConfig && (
        <SuccessNotification
          isOpen={showSuccessNotification}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessConfig(null);
          }}
          title={successConfig.title}
          message={successConfig.message}
          nextStep={successConfig.nextStep ? {
            ...successConfig.nextStep,
            buttonText: successConfig.nextStep.buttonText,
            description: successConfig.nextStep.description,
            path: null // We handle navigation manually
          } : null}
          notificationType="AG_CREATED"
          autoCloseDelay={10000}
        />
      )}
    </div>
    </>
  );
};

export default Assemblees;
